import { cn } from "@/lib/utils"
import { getInitials, getAvatarColor } from "@/lib/avatar"
import { getUserDisplayName } from "@/lib/user-display"
import { useAuthStore } from "@/stores/auth-store"

interface UserAvatarGroupProps {
  userIds: string[]
  users?: { id: string; fullName?: string | null | undefined; avatarUrl?: string | null | undefined }[]
  maxDisplay?: number
  size?: "sm" | "md"
}

function UserAvatarGroup({
  userIds,
  users = [],
  maxDisplay = 3,
  size = "sm",
}: UserAvatarGroupProps) {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const sizeClasses = size === "sm" ? "h-6 w-6" : "h-8 w-8"
  const textClasses = size === "sm" ? "text-[10px]" : "text-xs"
  const marginClass = size === "sm" ? "-ml-1.5" : "-ml-2"

  const visibleIds = userIds.slice(0, maxDisplay)
  const overflowCount = userIds.length - maxDisplay

  const userMap = new Map(users.map((u) => [u.id, u]))

  if (userIds.length === 0) return null

  return (
    <div className="flex items-center">
      {visibleIds.map((userId, index) => {
        const user = userMap.get(userId)
        const hasAvatar = user?.avatarUrl

        const color = getAvatarColor(userId)
        const displayLabel = getUserDisplayName(
          { userId, fullName: user?.fullName },
          currentUserId,
          { fallback: 'User' }
        )

        return (
          <div
            key={userId}
            className={cn(
              "rounded-full border-2 border-background flex items-center justify-center shrink-0",
              sizeClasses,
              index > 0 && marginClass
            )}
            title={displayLabel}
          >
            {hasAvatar ? (
              <img
                src={user.avatarUrl!}
                alt={displayLabel}
                className={cn("rounded-full object-cover", sizeClasses)}
              />
            ) : (
              <div
                className={cn(
                  "rounded-full flex items-center justify-center font-medium",
                  color.bg,
                  color.text,
                  sizeClasses,
                  textClasses
                )}
              >
                {getInitials(user?.fullName)}
              </div>
            )}
          </div>
        )
      })}
      {overflowCount > 0 && (
        <div
          className={cn(
            "rounded-full border-2 border-background bg-muted text-muted-foreground flex items-center justify-center shrink-0 font-medium",
            sizeClasses,
            textClasses,
            marginClass
          )}
        >
          +{overflowCount}
        </div>
      )}
    </div>
  )
}

export { UserAvatarGroup }
export type { UserAvatarGroupProps }
